import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DependencyType, Task } from '../api/types';
import { formatPredecessorsText, parsePredecessorsText } from '../utils/predecessors';

interface Row {
  type: DependencyType;
  lagDays: number;
}

export function PredecessorEditorModal({
  task,
  allTasks,
  onClose,
  onSave,
}: {
  task: Task;
  allTasks: Task[];
  onClose: () => void;
  onSave: (text: string) => void;
}) {
  const { t } = useTranslation();
  const initial = parsePredecessorsText(task.predecessorsText);
  const [selected, setSelected] = useState<Map<number, Row>>(
    new Map(initial.map((e) => [e.number, { type: e.type, lagDays: e.lagDays }])),
  );

  const candidates = allTasks.filter((c) => c.id !== task.id).sort((a, b) => a.number - b.number);

  const toggle = (number: number) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(number)) next.delete(number);
      else next.set(number, { type: 'FD', lagDays: 0 });
      return next;
    });
  };

  const updateRow = (number: number, patch: Partial<Row>) => {
    setSelected((prev) => {
      const next = new Map(prev);
      const current = next.get(number);
      if (current) next.set(number, { ...current, ...patch });
      return next;
    });
  };

  const handleSave = () => {
    const entries = [...selected.entries()]
      .sort(([a], [b]) => a - b)
      .map(([number, row]) => ({ number, type: row.type, lagDays: row.lagDays }));
    onSave(formatPredecessorsText(entries));
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal card" style={{ width: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            {t('tasks.predecessorsOf')} #{task.number} {task.nameFr}
          </h3>
          <button className="btn" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>

        <table>
          <thead>
            <tr>
              <th></th>
              <th>#</th>
              <th>{t('tasks.nameFr')}</th>
              <th>{t('tasks.linkType')}</th>
              <th>{t('tasks.lag')}</th>
            </tr>
          </thead>
          <tbody>
            {candidates.map((c) => {
              const row = selected.get(c.number);
              return (
                <tr key={c.id}>
                  <td>
                    <input type="checkbox" checked={!!row} onChange={() => toggle(c.number)} />
                  </td>
                  <td>{c.number}</td>
                  <td>{c.nameFr}</td>
                  <td>
                    <select
                      disabled={!row}
                      value={row?.type ?? 'FD'}
                      onChange={(e) => updateRow(c.number, { type: e.target.value as DependencyType })}
                    >
                      <option value="FD">FD</option>
                      <option value="FF">FF</option>
                      <option value="DF">DF</option>
                      <option value="DD">DD</option>
                    </select>
                  </td>
                  <td>
                    <input
                      type="number"
                      style={{ width: 64 }}
                      disabled={!row}
                      value={row?.lagDays ?? 0}
                      onChange={(e) => updateRow(c.number, { lagDays: parseInt(e.target.value || '0', 10) })}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="toolbar" style={{ marginTop: 12 }}>
          <button className="btn btn-primary" onClick={handleSave}>
            {t('common.confirm')}
          </button>
          <button className="btn" onClick={onClose}>
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}
